/* eslint-disable sonarjs/cognitive-complexity */
import * as yup from "yup";
import React, { useEffect, useRef, useState } from "react";
import StepLoading from "../StepLoading";
import MaskedTextField from "@/components/WebForm/FormUI/MaskedTextField";
import TooltipBody from "@/components/WebForm/FormUI/TooltipBody";
import { useDataFormContext } from "@/context/FormDataContext";
import { cleanNonDigits, validCpf } from "@/libs/utils";
import {
  getCnpjInputMask,
  sanitizeCnpj,
  validateCnpj,
} from "@consorciei/utils/cnpj";
import { getFastinExistsOffer } from "@/services/fastin";
import { register } from "@/services/lead";
import { IRegisterResponse } from "@/services/schemas";
import { LoadingButton } from "@mui/lab";
import {
  Autocomplete,
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormGroup,
  Grid2,
  TextField,
} from "@mui/material";
import { ErrorMessage, Form, Formik } from "formik";
import { TextStyled, TitleStyled, WrapperStyled } from "./styles";

interface ICheckFastinExistsOffer {
  status?: boolean;
  message?: string;
  waitingProposal?: boolean;
  hasProposal?: boolean;
  fastinUrl?: string;
}

const mandatory = "Campo obrigatório";
const minCharater = "Deve ter no mínimo 11 caracteres";
const cpfInvalid = "CPF Inválido";
const CNPJ_MASK = getCnpjInputMask();
/**
 *
 * @returns PartnerShareDetails components
 */
export default function PartnerShareDetails(): React.JSX.Element {
  const [isLoading, setIsLoading] = useState(false);
  const {
    isPj,
    currentAdm,
    dataForm,
    metaDataTimes,
    setMetaDataTimes,
    setCurrentFormStep,
    setCurrentFormVariation,
    setDataForm,
    setPlatformUrl,
  } = useDataFormContext();

  const INITIAL_FORM_STATE = {
    isPj: isPj,
    groupNumber: "",
    shareNumber: "",
    contractId: "",
    cpf: dataForm.cpf || "",
    cnpj: dataForm.cnpj || "",
    termAuthorizationData: false,
    nameOwner1:
      dataForm.companyOwners !== undefined
        ? dataForm.companyOwners[0].name
        : "",
    cpfOwner1:
      dataForm.companyOwners !== undefined ? dataForm.companyOwners[0].cpf : "",
    emailOwner1:
      dataForm.companyOwners !== undefined
        ? dataForm.companyOwners[0].email
        : "",
    nameOwner2:
      dataForm.companyOwners !== undefined && dataForm.companyOwners.length > 1
        ? dataForm.companyOwners[1].name
        : "",
    cpfOwner2:
      dataForm.companyOwners !== undefined && dataForm.companyOwners.length > 1
        ? dataForm.companyOwners[1].cpf
        : "",
    emailOwner2:
      dataForm.companyOwners !== undefined && dataForm.companyOwners.length > 1
        ? dataForm.companyOwners[1].email
        : "",
  };

  type FormValues = yup.InferType<typeof FORM_VALIDATION>;

  const FORM_VALIDATION = yup.object().shape({
    groupNumber: yup.string().min(1, mandatory).nullable().required(mandatory),
    shareNumber: yup
      .string()
      .test("shareNumberIsValid", "Cota inválida", function (x) {
        return x ? RegExp(currentAdm.sharePattern).test(x) : false;
      })
      .required(mandatory),
    cpf: yup.string().when("isPj", {
      is: (val: boolean) => val === true,
      then: (schema) => schema,
      otherwise: (schema) =>
        schema
          .min(11, minCharater)
          .test("validCpf", cpfInvalid, function (x) {
            return validCpf(x);
          })
          .required(mandatory),
    }),
    cnpj: yup.string().when("isPj", {
      is: (val: boolean) => val === true,
      then: (schema) =>
        schema
          .test("validCnpj", "CNPJ Inválido", function (x) {
            return validateCnpj(x ?? "").valid;
          })
          .required(mandatory),
      otherwise: (schema) => schema,
    }),
    nameOwner1: yup.string().when("isPj", {
      is: (val: boolean) => val === true,
      then: (schema) => schema.min(2, "Nome incompleto").required(mandatory),
      otherwise: (schema) => schema,
    }),
    cpfOwner1: yup.string().when("isPj", {
      is: (val: boolean) => val === true,
      then: (schema) =>
        schema
          .min(11, minCharater)
          .test("validCpf", cpfInvalid, function (x) {
            return validCpf(x);
          })
          .required(mandatory),
      otherwise: (schema) => schema,
    }),
    emailOwner1: yup.string().when("isPj", {
      is: (val: boolean) => val === true,
      then: (schema) =>
        schema.email("Insira um email válido").required(mandatory),
      otherwise: (schema) => schema,
    }),
    nameOwner2: yup.string().min(2, "Nome incompleto"),
    cpfOwner2: yup.string().min(11, minCharater),
    emailOwner2: yup.string().email("Insira um email válido"),
    termAuthorizationData: yup
      .boolean()
      .oneOf([true], "Campo deve ser preenchido")
      .required("Campo deve ser preenchido"),
    contractId: yup
      .string()
      .max(255, "Deve ter no máximo 255 caracteres")
      .nullable(true),
  });

  const checkFastinExistsOffer = async (values: FormValues) => {
    const fastinCheckExistsPayload = {
      admId: currentAdm.id as string,
      group: values.groupNumber as string,
      share: values.shareNumber as string,
      documentNumber: dataForm.isPj
        ? sanitizeCnpj((values.cnpj as string) || "")
        : cleanNonDigits((values.cpf as string) || ""),
      name: dataForm.isPj
        ? (values.nameOwner1 as string) || ""
        : (dataForm.name as string) || "",
      email: dataForm.isPj
        ? (values.emailOwner1 as string) || ""
        : (dataForm.email as string) || "",
    };

    try {
      const response = await getFastinExistsOffer(fastinCheckExistsPayload);
      const fastinExistsOfferResponse: ICheckFastinExistsOffer =
        response.data || {};
      setPlatformUrl(fastinExistsOfferResponse?.fastinUrl || "");
      return fastinExistsOfferResponse;
    } catch (error) {
      console.error(error);
    }
  };

  /**
   * @param values
   */
  async function submitForm(values: any) {
    setIsLoading(true);

    const formPayload = {
      ...dataForm,
      ...{
        formStep: 2,
        isPartnerAdm: true,
        group: values.groupNumber,
        share: values.shareNumber,
        cpf: (values.cpf ? values.cpf : cleanNonDigits(values.cpfOwner1)) || "",
        cnpj: sanitizeCnpj(values.cnpj || ""),
        lgpd: values.termAuthorizationData,
        contractId: values?.contractId || null,
        companyOwners: [
          {
            name: values.nameOwner1 || "",
            cpf: cleanNonDigits(values.cpfOwner1) || "",
            email: values.emailOwner1 || "",
          },
          {
            name: values.nameOwner2 || "",
            cpf: cleanNonDigits(values.cpfOwner2) || "",
            email: values.emailOwner2 || "",
          },
        ],
        metadata: {
          ...dataForm.metadata,
          formData: {
            ...metaDataTimes,
            finishedForm: String(new Date().valueOf()),
          },
        },
      },
    };

    setDataForm(formPayload);

    const EXTRACT_OR_FORM_END_STEP = 2;
    const PROPOSAL_FOUND_STEP = 4;
    const EXTERNAL_API_STEP = 5;
    const PROPOSAL_REFUSED_STEP = 7;

    try {
      const [registerResponse, newFastinExistsOffer] = await Promise.all([
        register(formPayload),
        checkFastinExistsOffer(values as FormValues),
      ]);
      const registerResponseData =
        registerResponse.data as unknown as IRegisterResponse;

      setDataForm((current) => ({
        ...current,
        ...{
          registrationId: registerResponseData.id,
          traceId: registerResponseData.traceId ?? current.traceId,
        },
      }));
      if (registerResponseData.id && newFastinExistsOffer) {
        const shouldNextStep =
          Object.keys(newFastinExistsOffer).length === 0 ||
          newFastinExistsOffer.waitingProposal ||
          !newFastinExistsOffer.status;
        if (shouldNextStep) {
          const shouldFinish = currentAdm.useAPI;
          if (shouldFinish) {
            setCurrentFormStep(EXTERNAL_API_STEP);
          } else {
            setCurrentFormStep(EXTRACT_OR_FORM_END_STEP);
          }
        } else if (newFastinExistsOffer.hasProposal) {
          setCurrentFormStep(PROPOSAL_FOUND_STEP);
        } else if (
          newFastinExistsOffer.status &&
          !newFastinExistsOffer.hasProposal
        ) {
          setCurrentFormStep(PROPOSAL_REFUSED_STEP);
        }
      } else {
        throw new Error("erro ao enviar dados");
      }
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  }

  const inputRef = useRef(null) as any;

  useEffect(() => {
    if (inputRef.current) {
      const height = inputRef?.current?.offsetHeight;
      window.parent.postMessage(
        ["setHeight", `${height + (height * 30) / 100}`],
        "*"
      );
    }
  }, []);

  return (
    <>
      {isLoading ? (
        <StepLoading text="Estamos processando os dados da sua cota, por favor aguarde..." />
      ) : (
        <Formik
          initialValues={INITIAL_FORM_STATE}
          validationSchema={FORM_VALIDATION}
          onSubmit={(values) => {
            submitForm(values);
          }}
        >
          {({
            values,
            errors,
            touched,
            handleBlur,
            handleChange,
            setFieldValue,
          }) => {
            return (
              <Form
                ref={inputRef}
                id={`formPartnerShareDetails${values.isPj ? "PJ" : "PF"}`}
              >
                <Grid2 container spacing={3}>
                  <Grid2 size={{ xs: 12 }}>
                    <TitleStyled>Dados da sua cota de consórcio</TitleStyled>
                    <TextStyled>
                      {
                        "Para avaliarmos a possibilidade de realizar uma oferta, precisaremos de alguns dados da cota e seu CPF. Com essas informações, solicitaremos os detalhes da sua carta junto à administradora do seu consórcio. Fique tranquilo, seus dados estarão protegidos conosco!"
                      }
                    </TextStyled>
                  </Grid2>

                  <Grid2 size={{ xs: 12, sm: 6 }}>
                    <FormGroup>
                      <Autocomplete
                        id="groupNumber"
                        options={dataForm.groupOptions ?? []}
                        onChange={(_e, value) => {
                          setFieldValue("groupNumber", value);
                        }}
                        onOpen={handleBlur}
                        noOptionsText="Opção não existente"
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            fullWidth
                            name="groupNumber"
                            label="Grupo"
                            error={
                              touched.groupNumber && Boolean(errors.groupNumber)
                            }
                            helperText={
                              touched.groupNumber && errors.groupNumber
                            }
                          />
                        )}
                      />
                      <TooltipBody content={currentAdm.groupDescription} />
                    </FormGroup>
                  </Grid2>

                  <Grid2 size={{ xs: 12, sm: 6 }}>
                    <TextField
                      name="shareNumber"
                      onChange={handleChange}
                      onBlur={(e: any) => {
                        handleBlur(e);
                        setFieldValue("shareNumber", values.shareNumber, true);
                      }}
                      value={values.shareNumber}
                      fullWidth
                      id="shareNumber"
                      label="Cota"
                      slotProps={{
                        input: {
                          inputMode: "numeric",
                        },
                      }}
                      error={touched.shareNumber && Boolean(errors.shareNumber)}
                      helperText={
                        errors.shareNumber
                          ? errors.shareNumber
                          : !values.shareNumber
                            ? "Sem o dígito"
                            : ""
                      }
                    />
                  </Grid2>

                  <Grid2 size={{ xs: 12 }}>
                    <TextField
                      name="contractId"
                      onChange={handleChange}
                      value={values.contractId}
                      fullWidth
                      id="contractId"
                      label="Contrato"
                      slotProps={{
                        input: {
                          inputMode: "numeric",
                          inputProps: {
                            maxLength: 255,
                          },
                        },
                      }}
                      helperText={"Este campo não é obrigatório."}
                    />
                  </Grid2>

                  <Grid2 size={{ xs: 12 }}>
                    <MaskedTextField
                      name={values.isPj ? "cnpj" : "cpf"}
                      mask={values.isPj ? CNPJ_MASK : "999.999.999-99"}
                      value={values.isPj ? values.cnpj : values.cpf}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      placeholder={
                        values.isPj ? CNPJ_MASK : "999.999.999-99"
                      }
                    >
                      <TextField
                        fullWidth
                        id={values.isPj ? "cnpj" : "cpf"}
                        name={values.isPj ? "cnpj" : "cpf"}
                        label={values.isPj ? "CNPJ" : "CPF"}
                        slotProps={{
                          input: {
                            inputMode: values.isPj ? "text" : "numeric",
                          },
                        }}
                        error={
                          values.isPj
                            ? touched.cnpj && Boolean(errors.cnpj)
                            : touched.cpf && Boolean(errors.cpf)
                        }
                        helperText={
                          values.isPj
                            ? touched.cnpj && errors.cnpj
                            : touched.cpf && errors.cpf
                        }
                      />
                    </MaskedTextField>
                  </Grid2>

                  {values.isPj && (
                    <>
                      <Grid2 size={{ xs: 12 }}>
                        <TitleStyled>Representantes Legais</TitleStyled>
                        <TextStyled>
                          Se a empresa possuir mais de um sócio, preencha os
                          dados de pelo menos dois representantes legais abaixo
                        </TextStyled>
                      </Grid2>
                      <Grid2 size={{ xs: 12, sm: 4 }}>
                        <TextField
                          size="small"
                          fullWidth
                          id="nameOwner1"
                          name="nameOwner1"
                          label="Nome completo"
                          value={values.nameOwner1}
                          onBlur={handleBlur}
                          onChange={handleChange}
                          error={
                            touched.nameOwner1 && Boolean(errors.nameOwner1)
                          }
                          helperText={touched.nameOwner1 && errors.nameOwner1}
                        />
                      </Grid2>
                      <Grid2 size={{ xs: 12, sm: 4 }}>
                        <MaskedTextField
                          name="cpfOwner1"
                          mask="999.999.999-99"
                          value={values.cpfOwner1}
                          onChange={handleChange}
                          onBlur={handleBlur}
                        >
                          <TextField
                            type="tel"
                            size="small"
                            fullWidth
                            id="cpfOwner1"
                            name="cpfOwner1"
                            label="CPF"
                            slotProps={{
                              input: {
                                inputMode: "numeric",
                              },
                            }}
                            error={
                              touched.cpfOwner1 && Boolean(errors.cpfOwner1)
                            }
                            helperText={touched.cpfOwner1 && errors.cpfOwner1}
                          />
                        </MaskedTextField>
                      </Grid2>
                      <Grid2 size={{ xs: 12, sm: 4 }}>
                        <TextField
                          size="small"
                          fullWidth
                          id="emailOwner1"
                          name="emailOwner1"
                          value={values.emailOwner1}
                          label="Email"
                          type="emailOwner1"
                          onBlur={handleBlur}
                          onChange={handleChange}
                          error={
                            touched.emailOwner1 && Boolean(errors.emailOwner1)
                          }
                          helperText={touched.emailOwner1 && errors.emailOwner1}
                        />
                      </Grid2>
                      <Grid2 size={{ xs: 12, sm: 4 }}>
                        <TextField
                          size="small"
                          fullWidth
                          id="nameOwner2"
                          name="nameOwner2"
                          label="Nome completo"
                          value={values.nameOwner2}
                          onBlur={handleBlur}
                          onChange={handleChange}
                          error={
                            touched.nameOwner2 && Boolean(errors.nameOwner2)
                          }
                          helperText={touched.nameOwner2 && errors.nameOwner2}
                        />
                      </Grid2>
                      <Grid2 size={{ xs: 12, sm: 4 }}>
                        <MaskedTextField
                          name="cpfOwner2"
                          mask="999.999.999-99"
                          value={values.cpfOwner2}
                          onChange={handleChange}
                          onBlur={handleBlur}
                        >
                          <TextField
                            type="tel"
                            size="small"
                            fullWidth
                            id="cpfOwner2"
                            name="cpfOwner2"
                            label="CPF"
                            slotProps={{
                              input: {
                                inputMode: "numeric",
                              },
                            }}
                            error={
                              touched.cpfOwner2 && Boolean(errors.cpfOwner2)
                            }
                            helperText={touched.cpfOwner2 && errors.cpfOwner2}
                          />
                        </MaskedTextField>
                      </Grid2>
                      <Grid2 size={{ xs: 12, sm: 4 }}>
                        <TextField
                          size="small"
                          fullWidth
                          id="emailOwner2"
                          name="emailOwner2"
                          label="Email"
                          type="emailOwner2"
                          value={values.emailOwner2}
                          onBlur={handleBlur}
                          onChange={handleChange}
                          error={
                            touched.emailOwner2 && Boolean(errors.emailOwner2)
                          }
                          helperText={touched.emailOwner2 && errors.emailOwner2}
                        />
                      </Grid2>
                    </>
                  )}

                  <Grid2 size={{ xs: 12 }}>
                    <FormControl>
                      <FormControlLabel
                        control={
                          <Checkbox
                            name="termAuthorizationData"
                            id="termAuthorizationData"
                            value={values.termAuthorizationData}
                            onBlur={handleBlur}
                            onChange={(e) => {
                              setMetaDataTimes({
                                ...metaDataTimes,
                                acceptedPartnerDataTerms: String(
                                  new Date().valueOf()
                                ),
                              });
                              handleChange(e);
                            }}
                          />
                        }
                        label={
                          "Ao continuar você autoriza a Consorciei a acessar as informações de sua cota inclusive o extrato completo com a administradora de consórcios e a autoriza a fornecer os dados de sua cota para a Consorciei"
                        }
                      />
                      <ErrorMessage
                        name="termAuthorizationData"
                        render={(msg) => <p className="errorMessage">{msg}</p>}
                      />
                    </FormControl>
                  </Grid2>

                  <Grid2 size={{ xs: 12 }}>
                    <WrapperStyled>
                      <Button
                        variant="goBack"
                        onClick={() => {
                          setCurrentFormStep(0);
                          setCurrentFormVariation("all");
                        }}
                      >
                        voltar
                      </Button>
                      <LoadingButton
                        loading={isLoading}
                        variant="goFoward"
                        type="submit"
                      >
                        continuar
                      </LoadingButton>
                    </WrapperStyled>
                  </Grid2>
                  {/* <pre>{JSON.stringify(errors, null, 4)}</pre> */}
                  {/* <pre>{JSON.stringify(values, null, 4)}</pre> */}
                </Grid2>
              </Form>
            );
          }}
        </Formik>
      )}
    </>
  );
}
